import T from '@/components/Helpers/Translate'
import { Skeleton } from '@/components/_ui/Skeleton'
import { ErrorAlert } from '@/components/_ui/ErrorAlert'
import { useProductRecommendationsQuery } from '@/lib/hooks/queries/cosmic/useProductRecommendationsQuery'
import CartSheetRecommendedProductsCarousel from './CartSheetRecommendedProductsCarousel/Carousel'
import CartSheetRecommendedProductsCarouselItem from './CartSheetRecommendedProductsCarousel/CarouselItem'

const CartSheetRecommendedProducts = () => {
  const {
    data: recommendationData,
    isLoading,
    error,
  } = useProductRecommendationsQuery({
    limit: 3,
    select: (data) => ({
      products: data.products.map((product) => ({
        slug: product.slug,
        name: product.name,
        imageUrl: product.imageUrl,
        image: product.image,
        variations: product.variations.slice(0, 1), // Only first variation
        subscriptionDiscount: product.subscriptionDiscount,
        description: product.description,
        purchaseOptions: product.purchaseOptions,
        policies: product.policies,
        categories: product.categories,
        attributes: product.attributes,
        availableFor: product.availableFor,
      })),
      errors: data.errors,
    }),
  })

  const recommendedProducts = recommendationData?.products || []
  const productErrors = recommendationData?.errors || []

  if (isLoading) {
    return (
      <div className="p-5">
        <span className="text-2xl text-primary">
          <T>you_might_also_like</T>
        </span>
        <div className="flex space-x-4">
          <Skeleton className="h-64 w-1/2 rounded-lg" />
          <Skeleton className="h-64 w-1/2 rounded-lg" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-5">
        <span className="text-2xl text-primary">
          <T>you_might_also_like</T>
        </span>
        <ErrorAlert
          error={{
            error: {
              code: 500,
              message: 'Failed to load product recommendations.',
            },
          }}
        />
      </div>
    )
  }

  if (!recommendedProducts || recommendedProducts.length === 0) {
    // Show errors if we have them but no products
    if (productErrors.length > 0) {
      return (
        <div className="p-5">
          <span className="text-2xl text-primary">
            <T>you_might_also_like</T>
          </span>
          <div className="mt-3 space-y-2">
            {productErrors.map((productError) => (
              <div
                key={productError.slug}
                className="rounded border border-orange-200 bg-orange-50 p-2"
              >
                <div className="text-sm text-orange-700">
                  <strong>{productError.slug}</strong>:{' '}
                  {productError.error.message}
                  {productError.error.code && (
                    <span className="ml-2 text-xs">
                      ({productError.error.code})
                    </span>
                  )}
                  {productError.error.httpStatus && (
                    <span className="ml-2 text-xs">
                      HTTP {productError.error.httpStatus}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    }
    return null
  }

  return (
    <div className="p-5">
      <span className="text-2xl text-primary">
        <T>you_might_also_like</T>
      </span>
      <CartSheetRecommendedProductsCarousel>
        {recommendedProducts.map((product, index) => (
          <CartSheetRecommendedProductsCarouselItem
            key={product.slug}
            product={product}
            index={index}
          />
        ))}
      </CartSheetRecommendedProductsCarousel>

      {/* Display product errors if any */}
      {productErrors.length > 0 && (
        <div className="mt-4 space-y-2">
          <div className="text-sm font-medium text-gray-700">
            <T>some_products_unavailable</T>:
          </div>
          {productErrors.map((productError) => (
            <div
              key={productError.slug}
              className="rounded border border-orange-200 bg-orange-50 p-2"
            >
              <div className="text-xs text-orange-700">
                <strong>{productError.slug}</strong>:{' '}
                {productError.error.message}
                {productError.error.code && (
                  <span className="ml-2">({productError.error.code})</span>
                )}
                {productError.error.httpStatus && (
                  <span className="ml-2">
                    HTTP {productError.error.httpStatus}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default CartSheetRecommendedProducts
